import React, { createContext, useContext, useState, useEffect } from 'react';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [blogContent, setBlogContent] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const defaultProducts = [
      {
        id: 1,
        name: 'Premium Wireless Headphones',
        price: 299.99,
        category: 'Electronics',
        description: 'High-quality wireless headphones with noise cancellation and premium sound quality.',
        image: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=400&h=400&fit=crop',
        stock: 15,
        featured: true
      },
      {
        id: 2,
        name: 'Organic Coffee Beans',
        price: 24.99,
        category: 'Food & Beverages',
        description: 'Premium organic coffee beans sourced from sustainable farms.',
        image: 'https://images.unsplash.com/photo-1559056199-641a0ac8b55e?w=400&h=400&fit=crop',
        stock: 50,
        featured: false
      },
      {
        id: 3,
        name: 'Minimalist Watch',
        price: 149.99,
        category: 'Accessories',
        description: 'Elegant minimalist watch with leather strap and precise movement.',
        image: 'https://images.unsplash.com/photo-1524592094714-0f0654e20314?w=400&h=400&fit=crop',
        stock: 8,
        featured: true
      },
      {
        id: 4,
        name: 'Eco-Friendly Water Bottle',
        price: 39.99,
        category: 'Lifestyle',
        description: 'Sustainable stainless steel water bottle with temperature control.',
        image: 'https://images.unsplash.com/photo-1602143407151-7111542de6e8?w=400&h=400&fit=crop',
        stock: 25,
        featured: false
      }
    ];

    const defaultBlogContent = `# Welcome to Our Store Blog\n\n## Discover Amazing Products and Stories\n\nWelcome to our curated collection of premium products.`;

    setTimeout(() => {
      setProducts(defaultProducts);
      setBlogContent(defaultBlogContent);
      setLoading(false);
    }, 500);
  }, []);

  const addProduct = (product) => {
    const newProduct = { ...product, id: Date.now(), stock: parseInt(product.stock) || 0, price: parseFloat(product.price) || 0 };
    setProducts(prev => [...prev, newProduct]);
  };

  const updateProduct = (id, updatedProduct) => {
    setProducts(prev => prev.map(product => product.id === id ? { ...product, ...updatedProduct } : product));
  };

  const deleteProduct = (id) => {
    setProducts(prev => prev.filter(product => product.id !== id));
  };

  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item => 
          item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
  };

  const removeFromCart = (id) => {
    setCart(prev => prev.filter(item => item.id !== id));
  };

  const updateCartQuantity = (id, delta) => {
    setCart(prev => prev.map(item => {
      if (item.id === id) {
        const newQty = Math.max(1, item.quantity + delta);
        return { ...item, quantity: newQty };
      }
      return item;
    }));
  };

  const clearCart = () => setCart([]);

  const processOrder = () => {
    // Update stock levels
    setProducts(prev => prev.map(p => {
      const cartItem = cart.find(item => item.id === p.id);
      if (cartItem) {
        return { ...p, stock: Math.max(0, p.stock - cartItem.quantity) };
      }
      return p;
    }));
    clearCart();
  };

  const value = {
    products,
    cart,
    blogContent,
    loading,
    addProduct,
    updateProduct,
    deleteProduct,
    addToCart,
    removeFromCart,
    updateCartQuantity,
    clearCart,
    processOrder,
    updateBlogContent: setBlogContent
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};